import { type SyntheticEvent, useState } from 'react';

import { z } from 'zod';

import { appConfig } from '@/shared/config/app-config';

import { signInWithPassword } from '../api/sign-in-with-password';
import { signInWithPasswordSchema } from '../model/sign-in-with-password.schema';

const genericErrorCopy = 'Something went wrong. Please try again.';

const defaultCooldownSeconds = 60;

type FieldName = 'email' | 'password';

type FieldErrors = {
    email?: string;
    password?: string;
    form?: string;
};

const fieldOrder: FieldName[] = ['email', 'password'];

export function useSignInWithPassword(role: string, returnUrl?: string) {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [errors, setErrors] = useState<FieldErrors>({});
    const [invalidCredentials, setInvalidCredentials] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [cooldown, setCooldown] = useState<{ until: number; seconds: number } | null>(null);
    const [locked, setLocked] = useState<{ email: string } | null>(null);

    const isCoolingDown = cooldown !== null;

    const validateField = (field: FieldName, value: string) => {
        const result = signInWithPasswordSchema.shape[field].safeParse(value);
        setErrors((prev) => ({
            ...prev,
            [field]: result.success ? undefined : result.error.issues[0]?.message,
        }));
    };

    const handleSubmit = async (
        event: SyntheticEvent<HTMLFormElement>,
    ): Promise<FieldName | undefined> => {
        event.preventDefault();
        if (isSubmitting || isCoolingDown) return;

        const parsed = signInWithPasswordSchema.safeParse({ email, password });
        if (!parsed.success) {
            const flattened = z.flattenError(parsed.error).fieldErrors;
            setInvalidCredentials(false);
            setErrors({
                email: flattened.email?.[0],
                password: flattened.password?.[0],
            });
            return fieldOrder.find((field) => flattened[field]?.[0]);
        }

        setErrors({});
        setInvalidCredentials(false);
        setIsSubmitting(true);

        const result = await signInWithPassword({ ...parsed.data, role, returnUrl });

        if (result.ok) {
            const fallback = new URL('/dashboard', appConfig.notesAppUrl).toString();
            window.location.href = result.returnUrl ?? fallback;
            return;
        }

        setIsSubmitting(false);
        if (result.code === 'INVALID_CREDENTIALS') {
            setInvalidCredentials(true);
            setErrors({});
            return 'email';
        }

        if (result.code === 'ACCOUNT_LOCKED') {
            setLocked({ email: result.email });
            setPassword('');
            setErrors({});
            setInvalidCredentials(false);
            return undefined;
        }

        if (result.code === 'RATE_LIMITED') {
            const seconds = result.retryAfterSeconds ?? defaultCooldownSeconds;
            setCooldown({ until: Date.now() + seconds * 1000, seconds });
            setPassword('');
            setErrors({});
            setInvalidCredentials(false);
            return undefined;
        }

        setErrors({ form: genericErrorCopy });
        return undefined;
    };

    const endCooldown = () => setCooldown(null);

    return {
        email,
        password,
        errors,
        invalidCredentials,
        isSubmitting,
        cooldown,
        isCoolingDown,
        endCooldown,
        locked,
        setEmail: (value: string) => {
            setEmail(value);
            if (errors.email || errors.form || invalidCredentials) {
                setErrors((prev) => ({ ...prev, email: undefined, form: undefined }));
                setInvalidCredentials(false);
            }
        },
        setPassword: (value: string) => {
            setPassword(value);
            if (errors.password || errors.form || invalidCredentials) {
                setErrors((prev) => ({ ...prev, password: undefined, form: undefined }));
                setInvalidCredentials(false);
            }
        },
        handleEmailBlur: () => validateField('email', email),
        handlePasswordBlur: () => validateField('password', password),
        handleSubmit,
    };
}
